import { Button } from "@/components/ui/button";
import { useTranslation } from "@/features/utils/i18n";
import {
  AgentField,
  CapabilitiesField,
  ChannelTypeField,
  StatusField,
} from "./fields-channel";
import { EmailAccountFields } from "./fields-email";
import { RoutingPanel } from "./fields-routing";
import { SipRegistrationFields } from "./fields-sip";
import { RegistrationBadge } from "../registration-badge";
import {
  HeyupNumbersField,
  TemporaryCallerNumbersField,
  TwilioCredentialsFields,
  TwilioWebhooksPanel,
} from "./fields-telephony";
import { WebChannelFields } from "./fields-web";
import { FORM_CHANNEL_TYPES } from "../channels";
import { useConnectorFormState } from "./state";
import type { ConnectorFormProps } from "./types";
import { validateConnectorForm } from "./validate";

export type { ConnectorFormData } from "./types";

/**
 * Create or edit one connector — a channel the agent answers on.
 *
 * The shape of the form is driven entirely by `type`: a HeyUp number picks from
 * the numbers we attributed, a Twilio one asks for credentials, a web one hands
 * back an embed snippet. This file is only the order those sections appear in;
 * the fields live in the `fields-*` modules, the state in `state.ts` and the
 * checks in `validate.ts`.
 */
export const ConnectorForm = ({
  mode,
  connector,
  channel,
  onSubmit,
  isSubmitting,
}: ConnectorFormProps) => {
  const { t } = useTranslation("connectors");
  const state = useConnectorFormState(connector, channel);
  const { type, capabilities } = state;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const data = validateConnectorForm(state, mode, t);
    if (!data) return;
    await onSubmit(data);
  };

  // The routing panel (caller rules, fallback, filters) applies to a client's
  // PBX too: the backend runs the same decision on its calls.
  const offeredTypes =
    mode === "create" && channel ? FORM_CHANNEL_TYPES[channel] : undefined;

  const isTelephony = type === "twilio" || type === "heyup" || type === "sip";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 py-4">
      {/* The client already chose the channel in the picker: a type field with a
          single possible answer (e-mail, web chat) would only ask it again. */}
      {offeredTypes?.length !== 1 && (
        <ChannelTypeField
          type={type}
          onChange={state.setType}
          disabled={mode === "edit"}
          canUseHeyupNumber={state.canUseHeyupNumber}
          hasTemporaryConnector={state.hasTemporaryConnector}
          isSysadmin={state.isSysadmin}
          offered={offeredTypes}
        />
      )}

      {mode === "edit" && (
        <StatusField status={state.status} onChange={state.setStatus} />
      )}

      <AgentField
        agentId={state.agentId}
        onChange={state.setAgentId}
        agents={state.agents}
      />

      {type === "heyup" && (
        <HeyupNumbersField
          selectableNumbers={state.heyupSelectableNumbers}
          selectedNumbers={state.selectedPhoneNumbers}
          onToggle={state.toggleHeyupNumber}
        />
      )}

      {isTelephony && (
        <>
          {type === "sip" && mode === "edit" && connector?.registration && (
            <RegistrationBadge
              registration={connector.registration}
              withHint={true}
            />
          )}

          {type === "sip" && (
            <SipRegistrationFields
              mode={mode}
              value={state.sipRegistration}
              onChange={state.updateSipRegistration}
              phoneNumber={state.phoneNumbers}
              onPhoneNumberChange={state.setPhoneNumbers}
            />
          )}

          {type === "twilio" && (
            <TwilioCredentialsFields
              mode={mode}
              capabilities={capabilities}
              phoneNumbers={state.phoneNumbers}
              onPhoneNumbersChange={state.setPhoneNumbers}
              accountId={state.accountId}
              onAccountIdChange={state.setAccountId}
              authToken={state.authToken}
              onAuthTokenChange={state.setAuthToken}
              twilioRegion={state.twilioRegion}
              onTwilioRegionChange={state.setTwilioRegion}
              whatsappNumbers={state.whatsappNumbers}
              onWhatsappNumbersChange={state.setWhatsappNumbers}
            />
          )}

          {capabilities.includes("voice") && (
            <RoutingPanel
              agents={state.agents}
              members={state.transferableMembers}
              users={state.routableUsers}
              isSysadmin={state.isSysadmin}
              routingRules={state.routingRules}
              onRuleChange={state.updateRoutingRule}
              onRuleAdd={state.addRoutingRule}
              onRuleRemove={state.removeRoutingRule}
              fallbackDestinations={state.fallbackDestinations}
              onFallbackChange={state.setFallbackDestinations}
              agentOffEnabled={state.agentOffEnabled}
              onAgentOffEnabledChange={state.setAgentOffEnabled}
              agentOffScheduleId={state.agentOffScheduleId}
              onAgentOffScheduleChange={state.setAgentOffScheduleId}
              ringBeforeAgentSeconds={state.ringBeforeAgentSeconds}
              onRingBeforeAgentSecondsChange={state.setRingBeforeAgentSeconds}
              geoFilter={state.geoFilter}
              onGeoFilterChange={state.setGeoFilter}
              outboundFilter={state.outboundFilter}
              onOutboundFilterChange={state.setOutboundFilter}
              demoNumber={state.demoNumber}
              onDemoNumberChange={state.setDemoNumber}
              codeRouting={state.codeRouting}
              onCodeRoutingChange={state.setCodeRouting}
              defaultConnector={state.defaultConnector}
              onDefaultConnectorChange={state.setDefaultConnector}
            />
          )}

          {type === "twilio" && <TwilioWebhooksPanel />}
        </>
      )}

      {type === "email" && (
        <EmailAccountFields
          mode={mode}
          value={state.emailAccount}
          onChange={state.updateEmailAccount}
        />
      )}

      {type === "temporary" && (
        <TemporaryCallerNumbersField
          callerNumbers={state.callerNumbers}
          onChange={state.setCallerNumbers}
        />
      )}

      {type === "web" && (
        <WebChannelFields
          mode={mode}
          connector={connector}
          clientId={state.clientId}
          allowedOrigins={state.allowedOrigins}
          onAllowedOriginsChange={state.setAllowedOrigins}
          webCallEnabled={state.webCallEnabled}
          onWebCallEnabledChange={state.setWebCallEnabled}
          publicCallbackEnabled={state.publicCallbackEnabled}
          onPublicCallbackEnabledChange={state.setPublicCallbackEnabled}
        />
      )}

      {/* Capabilities — hidden for the types that carry a single one: web (chat),
          temporary (voice) and email */}
      {type !== "web" && type !== "temporary" && type !== "email" && (
        <CapabilitiesField
          type={type}
          capabilities={capabilities}
          onToggle={state.toggleCapability}
        />
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting
            ? mode === "create"
              ? t("connectors.form.creating")
              : t("connectors.form.updating")
            : mode === "create"
              ? t("connectors.form.createButton")
              : t("connectors.form.updateButton")}
        </Button>
      </div>
    </form>
  );
};
